import type { Brand, Category, Tag } from '@/payload-types';
import Link from 'next/link';
import React, { CSSProperties } from 'react';
import {Link as TLink} from '@/payload-types';

import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from 'src/utilities/cn';

export type CMSLinkType = Omit<TLink, 'label'> & {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string
  size?: ButtonProps['size'] | null
  style?: CSSProperties,
  params?: {
    showParams?: boolean;
    categories?: (string | Category)[];
    tags?: (string | Tag)[];
    brands?: (string | Brand)[];
  }
}

const categoryMap = {
  'categories': 'collections'
};

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
    style,
    params,
  } = props;

  const getCategoryLink = (link: string) => {
    return categoryMap[link] || link;
  };

  let href =
    type === 'reference' && typeof reference?.value === 'object' && reference.value.slug
      ? `${reference?.relationTo !== 'pages' ? `/${getCategoryLink(reference?.relationTo)}` : ''}/${reference.value.slug}`
      : url;

  if (href === '/home') href = '/';

  if (params?.showParams) {
    const { categories, tags, brands } = params;
    const searchParams = new URLSearchParams();

    const categoryParams = categories?.map(cat => {
      return (cat && typeof cat === 'object') ? cat.id : cat;
    }).filter(Boolean).join(',');

    const tagParams = tags?.map(tag => {
      return (tag && typeof tag === 'object') ? tag.id : tag;
    }).filter(Boolean).join(',');

    const brandParams = brands?.map(brand => {
      return (brand && typeof brand === 'object') ? brand.id : brand;
    }).filter(Boolean).join(',');

    if (categoryParams) searchParams.append('c', categoryParams);
    if (tagParams) searchParams.append('t', tagParams);
    if (brandParams) searchParams.append('b', brandParams);

    href += `?${searchParams.toString()}`;
  }

  if (!href) return null;

  const size = appearance === 'link' ? 'clear' : sizeFromProps;
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {};

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link className={cn(className)} href={href || url || ''} style={style} {...newTabProps}>
        {label && label}
        {children && children}
      </Link>
    );
  }

  return (
    <Button asChild className={className} size={size} variant={appearance}>
      <Link className={cn(className)} href={href || url || ''} style={style} {...newTabProps}>
        {label && label}
        {children && children}
      </Link>
    </Button>
  );
};
